import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Wallet, User, Star, Settings, HelpCircle, X, ChevronRight, ChevronLeft, Sparkles, Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  {
    title: "Welcome to Rahe Portal!",
    description: "Let us give you a quick tour of your guide dashboard. This will only take a moment.",
    icon: Sparkles,
    color: "bg-blue-500",
    path: null,
    sidebarId: null,
  },
  {
    title: "Dashboard",
    description: "Your command center. See critical intel about today's tours, manage incoming tour offers (accept or decline), and track your monthly earnings at a glance.",
    icon: LayoutDashboard,
    color: "bg-indigo-500",
    path: "/dashboard",
    sidebarId: "sidebar-dashboard",
  },
  {
    title: "Bookings",
    description: "View and manage all your active and past bookings. Search, sort, and click any booking to see full details, message customers, or add tours to your calendar.",
    icon: CalendarDays,
    color: "bg-teal-500",
    path: "/bookings",
    sidebarId: "sidebar-bookings",
  },
  {
    title: "Reviews",
    description: "Check your customer reviews and ratings. See your average score and read detailed feedback from tourists.",
    icon: Star,
    color: "bg-amber-500",
    path: "/reviews",
    sidebarId: "sidebar-reviews",
  },
  {
    title: "Notifications",
    description: "Stay updated with tour requests, assignments, payment confirmations, new reviews, and important announcements.",
    icon: Bell,
    color: "bg-pink-500",
    path: "/notifications",
    sidebarId: "sidebar-notifications",
  },
  {
    title: "Profile",
    description: "Complete your profile to start receiving tour offers. Upload a photo, add your languages, cities, and certifications.",
    icon: User,
    color: "bg-violet-500",
    path: "/profile",
    sidebarId: "sidebar-profile",
  },
  {
    title: "Payments",
    description: "Manage your payment method (Bank, PayPal, or Revolut) and view all processed payments.",
    icon: Wallet,
    color: "bg-emerald-500",
    path: "/finance",
    sidebarId: "sidebar-finance",
  },
  {
    title: "Settings",
    description: "Change your language, manage push notifications, update your password, or mark yourself inactive when you need a break.",
    icon: Settings,
    color: "bg-orange-500",
    path: "/settings",
    sidebarId: "sidebar-settings",
  },
  {
    title: "Help",
    description: "Need assistance? Visit the Help page to contact support anytime.",
    icon: HelpCircle,
    color: "bg-cyan-500",
    path: "/help",
    sidebarId: "sidebar-help",
  },
  {
    title: "You're all set!",
    description: "Start by completing your profile, then you'll begin receiving tour offers on your dashboard. Happy guiding!",
    icon: Sparkles,
    color: "bg-emerald-500",
    path: "/dashboard",
    sidebarId: null,
  },
];

export default function OnboardingTour() {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const shouldShow = localStorage.getItem("rahe_show_onboarding");
    if (shouldShow === "true" && location.pathname === "/dashboard") {
      const timer = setTimeout(() => setActive(true), 800);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Highlight the sidebar item for the current step
  useEffect(() => {
    if (!active) return;
    const current = STEPS[step];
    
    // Remove all highlights first
    document.querySelectorAll("[data-testid^='sidebar-']").forEach((el) => {
      el.style.outline = "";
      el.style.outlineOffset = "";
      el.style.borderRadius = "";
      el.style.boxShadow = "";
      el.style.position = "";
      el.style.zIndex = "";
    });

    // Add highlight to current sidebar item
    if (current.sidebarId) {
      const sidebarEl = document.querySelector(`[data-testid='${current.sidebarId}']`);
      if (sidebarEl) {
        sidebarEl.style.outline = "2px solid #FBBF24";
        sidebarEl.style.outlineOffset = "2px";
        sidebarEl.style.borderRadius = "8px";
        sidebarEl.style.boxShadow = "0 0 12px rgba(251, 191, 36, 0.5)";
        sidebarEl.style.position = "relative";
        sidebarEl.style.zIndex = "10000";
        sidebarEl.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
      }
    }

    return () => {
      document.querySelectorAll("[data-testid^='sidebar-']").forEach((el) => {
        el.style.outline = "";
        el.style.outlineOffset = "";
        el.style.borderRadius = "";
        el.style.boxShadow = "";
        el.style.position = "";
        el.style.zIndex = "";
      });
    };
  }, [active, step]);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      const nextStep = step + 1;
      setStep(nextStep);
      if (STEPS[nextStep].path) navigate(STEPS[nextStep].path);
    } else {
      handleDismiss();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      const prevStep = step - 1;
      setStep(prevStep);
      if (STEPS[prevStep].path) navigate(STEPS[prevStep].path);
    }
  };

  const handleDismiss = () => {
    setActive(false);
    localStorage.removeItem("rahe_show_onboarding");
    navigate("/dashboard");
  };

  if (!active) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Card */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slideUp"
          data-testid="onboarding-card"
        >
          {/* Header with color bar */}
          <div className={`${current.color} px-6 py-5 flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium">
                  Step {step + 1} of {STEPS.length}
                </p>
                <h3 className="text-white font-semibold text-base">{current.title}</h3>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="onboarding-dismiss"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <div
              className={`h-full ${current.color} transition-all duration-500 ease-out`}
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>

          {/* Body */}
          <div className="p-6">
            <p className="text-sm text-slate-600 leading-relaxed">{current.description}</p>
            {current.sidebarId && (
              <p className="text-xs text-blue-600 mt-3 font-medium">
                Check the highlighted tab in the sidebar
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-5 flex items-center justify-between">
            <button
              onClick={handleDismiss}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="onboarding-skip"
            >
              Skip tour
            </button>
            <div className="flex gap-2">
              {!isFirst && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  className="h-9 px-3"
                  data-testid="onboarding-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className={`h-9 px-4 ${current.color} hover:opacity-90 text-white`}
                data-testid="onboarding-next"
              >
                {isLast ? "Get Started" : "Next"} {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
